import SideModal from "/src/components/modal/SideModal/SideModal";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import { useToastDispatchContext } from '/src/layouts/toast/context/toastContext';
import "./ConversationTranscriptionModal.css";
import TranscriptionBubble from "../ConversationProfil/TranscriptionBubble/TranscriptionBubble";
import { useState, useEffect, useMemo } from "react";
import InputSearch from "/src/components/UI/InputSearch/InputSearch";
import { useMode } from "../ConversationProfil/ConversationCard/ModeContext/ModeContext";
import { FaDownload } from "react-icons/fa";
import jsPDF from "jspdf";
import ConversationCompletedService from "/src/features/supervision/conversations/services/ConversationCompletedService";
import storage from "/src/utils/storage";
import hasPermission from "/src/utils/hasPermission";
import canMakeReview from "/src/features/supervision/conversations/utils/canMakeReview";

const PDF_COLOR_MAIN = [14, 122, 237];
const PDF_COLOR_WHITE = [255, 255, 255];
const PDF_COLOR_DARK = [67, 70, 86];
const PDF_COLOR_LIGHT_BG = [245, 249, 255];
const PDF_COLOR_LIGHT_BORDER = [225, 232, 240];
const PDF_COLOR_MUTED = [140, 150, 165];
const ConversationTranscriptionModal = NiceModal.create(({ conversation, setConversation, audioRef, onOpen, onCloseFromMenu, scrollToStart }) => {
    const { mode } = useMode();
    const modal = useModal();
    const toast = useToastDispatchContext();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const { onClose } = useModalHelper(modal);
    const { getCriterias, addCriteriaDetection } = ConversationCompletedService;
    const currentUser = storage('local').get('user');
    const canExportConversationData = currentUser?.is_admin || hasPermission('export_conversation_data');

    const [criteriaCatalog, setCriteriaCatalog] = useState([]);
    const unitId = conversation?.data?.unit_id;

    useEffect(() => {
        if (!unitId) return;
        const fetchCriterias = async () => {
            const [response, error] = await getCriterias({ unit_id: unitId });
            if (error || !response) {
                console.error("Failed to fetch criterias:", error);
                return;
            }
            setCriteriaCatalog(response);
        };
        fetchCriterias();
    }, [unitId]);

    const criteriaOptions = useMemo(
        () => criteriaCatalog.map(c => ({ value: c.id, label: c.name })),
        [criteriaCatalog]
    );

    const handleAddCriteria = async (msg, criteriaId) => {
        const [response, error] = await addCriteriaDetection(conversation?.data?.call_id, criteriaId, {
            segment_index: msg.segment_index
        });
        if (error) {
            console.error("Failed to add criteria detection:", error);
            toast.error("Error", error?.detail || "Failed to add the criteria.");
            return;
        }

        toast.success("Criteria Added", "The criteria has been added to this segment.");

        setConversation?.(prev => {
            const addToSide = (segments, side) =>
                (segments?.[side] || []).map(seg =>
                    seg.segment_index === msg.segment_index
                        ? { ...seg, criterias: [...(seg.criterias || []), criteriaId] }
                        : seg
                );
            const patchSegments = (segments) =>
                segments && { left: addToSide(segments, "left"), right: addToSide(segments, "right") };

            const hasRow = prev.criterias?.some(c => c.id === response.id);
            const criterias = hasRow
                ? prev.criterias.map(c => c.id === response.id ? { ...c, detection: response.detection } : c)
                : [...(prev.criterias || []), response];

            return {
                ...prev,
                criterias,
                data: {
                    ...prev.data,
                    content: patchSegments(prev.data.content),
                    translate_content: patchSegments(prev.data.translate_content),
                    score: response.conversation_score
                }
            };
        });
    };

    const handleClose = () => {
        onCloseFromMenu();
        onClose();
    }
    const getMessages = () => {
        if (!conversation?.data?.content) return [];
        var data = conversation?.data?.content
        if (mode === "translated") {
            data = conversation?.data?.translate_content
        }

        return [
            ...(data?.left || []).map(t => ({
                ...t,
                side: "client"
            })),
            ...(data?.right || []).map(t => ({
                ...t,
                side: "agent"
            })),
        ];
    };

    const messages = useMemo(() => {
        const data = mode === "translated"
            ? conversation?.data?.translate_content
            : conversation?.data?.content;

        return [
            ...(data?.left || []).map(t => ({ ...t, side: "client" })),
            ...(data?.right || []).map(t => ({ ...t, side: "agent" })),
        ].sort((a, b) => a.start - b.start);
    }, [conversation, mode]);

    useEffect(() => {
        if (scrollToStart == null) return;

        if (audioRef?.current?.audio?.current) {
            audioRef.current.audio.current.currentTime = scrollToStart;
        }

        const timeout = setTimeout(() => {
            const target = document.querySelector(`.conversation-transcription [data-start="${scrollToStart}"]`);
            target?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);

        return () => clearTimeout(timeout);
    }, [scrollToStart, messages]);

    // messages + toutes les occurrences de searchTerm
    const highlightedMessages = messages.map(msg => {
        if (!searchTerm) return { ...msg, highlighted: [] };

        const regex = new RegExp(searchTerm, "gi");
        const matches = [...msg.text.matchAll(regex)];
        return { ...msg, highlighted: matches };
    });

    const handleMovingSearchResult = (incr) => {
        if (highlightedMessages.flatMap(m => m.highlighted).length === 0) return;
        setCurrentMatchIndex((prev) =>
            (prev + incr) % highlightedMessages.flatMap(m => m.highlighted).length
        )
    }

    useEffect(() => {
        if (currentMatchIndex >= 0) {
            const currentMatch = document.querySelectorAll(".transcription-bubble mark")[currentMatchIndex];
            currentMatch?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [currentMatchIndex]);

    const [time, setTime] = useState(0);

    useEffect(() => {
        const audio = audioRef?.current?.audio?.current;
        if (!audio) return;

        const updateTime = () => {
            setTime(audio.currentTime);
        };

        audio.addEventListener("timeupdate", updateTime);

        return () => {
            audio.removeEventListener("timeupdate", updateTime);
        };
    }, [audioRef]);

    useEffect(() => {
        if (modal == null) return;
        onOpen(modal);
    }, [modal])

    useEffect(() => {
        setSearchTerm("");
        setCurrentMatchIndex(0);
    }, [mode]);

    const [internalMode, setInternalMode] = useState(mode);
    useEffect(() => {
        setInternalMode(mode);
    }, [mode]);

    const formatTime = (time) => {
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    const handleExportTranscription = () => {
        const data = conversation?.data;
        const callId = data?.call_id || "conversation";

        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 40;
        const contentTop = 130;
        const contentBottom = pageHeight - 40;

        const drawHeader = () => {
            doc.setFillColor(...PDF_COLOR_MAIN);
            doc.rect(0, 0, pageWidth, 95, "F");

            doc.setTextColor(...PDF_COLOR_WHITE);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(18);
            doc.text("Conversation Transcript", marginX, 38);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(`Call ID : ${callId}`, marginX, 60);
            doc.text(`Agent : ${data?.agent_id || "-"}`, marginX, 76);

            const dateStr = new Date(data?.timestamp || data?.created_at).toLocaleString("fr-FR");
            const rightX = pageWidth - marginX;
            doc.text(`Contact : ${data?.meta_data?.contact_id || "-"}`, rightX, 60, { align: "right" });
            doc.text(`Date : ${dateStr}`, rightX, 76, { align: "right" });
        };

        const drawFooter = (pageNumber) => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(...PDF_COLOR_MUTED);
            doc.text(`Page ${pageNumber}`, pageWidth - marginX, pageHeight - 20, { align: "right" });
        };

        let pageNumber = 1;
        drawHeader();
        drawFooter(pageNumber);
        let y = contentTop;

        const padding = 10;
        const gap = 12;
        const lineHeight = 13;
        const maxBubbleWidth = (pageWidth - marginX * 2) * 0.72;

        messages.forEach((msg) => {
            const isAgent = msg.side === "agent";
            const speaker = isAgent ? "Agent" : "Client";
            const label = `${speaker}  ·  ${formatTime(msg.start)} - ${formatTime(msg.end)}`;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10.5);
            const lines = doc.splitTextToSize(msg.text || "", maxBubbleWidth - padding * 2);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(8.5);
            const labelWidth = doc.getTextWidth(label);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10.5);
            const textWidth = lines.reduce((max, line) => Math.max(max, doc.getTextWidth(line)), 0);

            const bubbleWidth = Math.min(maxBubbleWidth, Math.max(textWidth, labelWidth) + padding * 2);
            const bubbleHeight = padding * 2 + 14 + lines.length * lineHeight;

            if (y + bubbleHeight > contentBottom) {
                doc.addPage();
                pageNumber += 1;
                drawHeader();
                drawFooter(pageNumber);
                y = contentTop;
            }

            const x = isAgent ? pageWidth - marginX - bubbleWidth : marginX;

            if (isAgent) {
                doc.setFillColor(...PDF_COLOR_LIGHT_BG);
                doc.setDrawColor(...PDF_COLOR_LIGHT_BORDER);
                doc.roundedRect(x, y, bubbleWidth, bubbleHeight, 8, 8, "FD");
                doc.setTextColor(...PDF_COLOR_MAIN);
            } else {
                doc.setFillColor(...PDF_COLOR_MAIN);
                doc.roundedRect(x, y, bubbleWidth, bubbleHeight, 8, 8, "F");
                doc.setTextColor(...PDF_COLOR_WHITE);
            }

            doc.setFont("helvetica", "bold");
            doc.setFontSize(8.5);
            if (!isAgent) doc.setTextColor(...PDF_COLOR_WHITE);
            doc.text(label, x + padding, y + padding + 6);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10.5);
            doc.setTextColor(...(isAgent ? PDF_COLOR_DARK : PDF_COLOR_WHITE));
            doc.text(lines, x + padding, y + padding + 20);

            y += bubbleHeight + gap;
        });

        doc.save(`transcription_${callId}.pdf`);
    };

    return (
        <SideModal id={modal.id} autoClose={false} onClose={handleClose} style={{ width: '550px' }} name="conversation_transcription_modal">
            <div className="transcription-modal-leave">
                {canExportConversationData && (
                    <div className="button-leave" id="export_transcription_container" title="Export transcription (PDF)" onClick={handleExportTranscription}>
                        <FaDownload />
                    </div>
                )}
                <div className="button-leave" id="close_modal_container" onClick={handleClose} >
                    <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
                </div>
            </div>
            <div className="conversation-search">
                <span className="search-count">
                    {highlightedMessages.flatMap(m => m.highlighted).length > 0
                        ? `${currentMatchIndex + 1} / ${highlightedMessages.flatMap(m => m.highlighted).length}`
                        : ""}
                </span>
                <InputSearch placeholderLibelle="Search in the conversation..." name={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <div className="search-controls">
                    <i className="fas fa-chevron-up button-search-movement" onClick={() => handleMovingSearchResult(-1)}></i>

                    <i className="fas fa-chevron-down button-search-movement" onClick={() => handleMovingSearchResult(1)}></i>

                </div>
            </div>

            <div className="conversation-transcription">
                {messages.map((msg, i) => (
                    <TranscriptionBubble
                        key={i}
                        msg={msg}
                        audioRef={audioRef}
                        searchTerm={searchTerm}
                        isActive={time >= msg.start && time <= msg.end}
                        isSelected={scrollToStart != null && msg.start === scrollToStart}
                        criteriaOptions={criteriaOptions}
                        onAddCriteria={canMakeReview() ? handleAddCriteria : undefined}
                    />
                ))}
            </div>
        </SideModal >
    )
});


export default ConversationTranscriptionModal;